import React, { useEffect, useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { answerDeskService, FaqEntry } from "../../../services/answerDeskService";
import { apiErrorMessage } from "../answerDeskUtils";

interface FlagDialogProps {
    entry: FaqEntry | null;
    onClose: () => void;
    onFlagged: (entry: FaqEntry) => void;
}

export const FlagDialog: React.FC<FlagDialogProps> = ({ entry, onClose, onFlagged }) => {
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (entry) {
            setNote("");
            setError(null);
            setSaving(false);
        }
    }, [entry]);

    const submit = async () => {
        if (!entry) return;
        if (!note.trim()) {
            setError("Say what looks wrong, so the manager knows what to fix.");
            return;
        }
        setSaving(true);
        try {
            onFlagged(await answerDeskService.flag(entry.faq_entry_id, note.trim()));
        } catch (err) {
            setError(apiErrorMessage(err, "Couldn't flag it. Try again."));
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!entry} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Flag as outdated</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {entry?.question}
                    </Typography>
                    {error && <Alert severity="error">{error}</Alert>}
                    <TextField
                        label="What looks wrong?"
                        placeholder="e.g. A customer says the Facebook page told them something different"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        multiline
                        minRows={3}
                        inputProps={{ maxLength: 500 }}
                        autoFocus
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5 }}>
                <Button onClick={onClose} disabled={saving} color="inherit">
                    Cancel
                </Button>
                <Button variant="contained" disableElevation onClick={submit} disabled={saving}>
                    Send to managers
                </Button>
            </DialogActions>
        </Dialog>
    );
};
